import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSpinner, FaCalendarAlt, FaCheckCircle, FaShieldAlt, FaIdCard } from "react-icons/fa";
import { doc, updateDoc } from "firebase/firestore";
import { db, auth } from "../pages/firebase";
import OnboardingLayout from "./OnboardingLayout";

const AgeVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    dateOfBirth: "",
    verificationStatus: "pending" // pending, processing, verified, failed
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(1);

  const calculateAge = (birthDate) => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const verifyBasicAge = () => {
    if (!formData.dateOfBirth) {
      setError("Please enter your date of birth");
      return false;
    }

    const birthDate = new Date(formData.dateOfBirth);
    const age = calculateAge(birthDate);

    if (age < 18) {
      setError("You must be at least 18 years old to use this service");
      return false;
    }

    return true;
  };

  const handleIDmeVerification = async () => {
    try {
      setFormData(prev => ({ ...prev, verificationStatus: "processing" }));
      
      // Generate a unique state parameter
      const state = Math.random().toString(36).substring(7);
      
      // Store the state in localStorage for verification
      localStorage.setItem('idme_state', state);
      
      // Construct the ID.me authorization URL
      const authUrl = new URL('https://api.id.me/oauth/authorize');
      authUrl.searchParams.append('client_id', process.env.REACT_APP_IDME_CLIENT_ID);
      authUrl.searchParams.append('redirect_uri', process.env.NODE_ENV === 'production' 
        ? 'https://foloapp.co.uk/onboarding/age-verification/callback'
        : 'http://localhost:3000/onboarding/age-verification/callback'
      );
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', 'openid identity');
      authUrl.searchParams.append('state', state);
      authUrl.searchParams.append('nonce', Math.random().toString(36).substring(7));
      authUrl.searchParams.append('acr_values', 'loa3');
      authUrl.searchParams.append('prompt', 'login');
      authUrl.searchParams.append('verification_type', 'identity');
      
      // Open ID.me verification in a new window
      const verificationWindow = window.open(
        authUrl.toString(),
        'ID.me Verification',
        'width=800,height=600'
      );

      // Listen for the callback
      const handleCallback = async (event) => {
        if (event.origin !== window.location.origin) return;
        
        const { code, state: callbackState } = event.data;
        
        // Verify the state parameter
        const storedState = localStorage.getItem('idme_state');
        if (!storedState || callbackState !== storedState) {
          setError('Invalid state parameter. Please try again.');
          setFormData(prev => ({ ...prev, verificationStatus: "failed" }));
          verificationWindow?.close();
          return;
        }

        try {
          // Exchange code for tokens
          const response = await fetch(`${process.env.REACT_APP_API_URL}/verifyIdme`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
            },
            body: JSON.stringify({ code, state: callbackState })
          });

          if (!response.ok) {
            throw new Error('ID.me verification failed');
          }

          const result = await response.json();
          
          // Update user data in Firestore
          const user = auth.currentUser;
          if (user) {
            await updateDoc(doc(db, 'users', user.uid), {
              ageVerified: true,
              dateOfBirth: formData.dateOfBirth,
              age: result.age,
              idVerified: true,
              verificationResult: {
                provider: "ID.me",
                timestamp: new Date().toISOString(),
                verifiedIdentity: true,
                verifiedAge: true
              },
              onboardingStep: 'user-type',
              lastUpdated: new Date().toISOString()
            });
          }

          // Close the verification window
          verificationWindow?.close();
          
          // Navigate to next step
          navigate("/onboarding/user-type", { 
            state: { 
              ...location.state,
              dateOfBirth: formData.dateOfBirth,
              ageVerified: true,
              idVerified: true
            } 
          });
        } catch (error) {
          setError(error.message);
          setFormData(prev => ({ ...prev, verificationStatus: "failed" }));
        } finally {
          // Clean up the state from localStorage
          localStorage.removeItem('idme_state');
        }
      };

      // Add event listener for the callback
      window.addEventListener('message', handleCallback, false);

      // Clean up event listener when component unmounts
      return () => {
        window.removeEventListener('message', handleCallback);
      };
    } catch (error) {
      setError(error.message);
      setFormData(prev => ({ ...prev, verificationStatus: "failed" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      if (verificationStep === 1) {
        if (verifyBasicAge()) {
          setVerificationStep(2);
          setIsLoading(false);
          return;
        }
        return;
      }

      // Start ID.me verification
      await handleIDmeVerification();
    } catch (error) {
      setError(error.message);
      setFormData(prev => ({ ...prev, verificationStatus: "failed" }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OnboardingLayout currentStep={1} totalSteps={5}>
      <div className="onboarding-form">
        <h1>Age Verification</h1>
        <p className="subtext">
          To comply with regulations, we need to verify your age and identity.
          <br />
          <small className="security-note">
            <FaShieldAlt /> Your identity will be verified through our secure partner ID.me
          </small>
        </p>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          {verificationStep === 1 ? (
            <div className="form-group">
              <label htmlFor="dateOfBirth">
                <FaCalendarAlt className="form-icon" /> Date of Birth
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                max={new Date().toISOString().split('T')[0]}
                className="form-input"
              />
            </div>
          ) : (
            <div className="verification-step">
              <h3>Identity Verification</h3>
              <p>
                We&apos;ll now verify your identity using ID.me. This process will:
              </p>
              <ul className="verification-steps">
                <li><FaIdCard /> Scan your government-issued ID (Driver&apos;s License, Passport, or State ID)</li>
                <li><FaCalendarAlt /> Confirm your age matches the date of birth you provided</li>
                <li><FaShieldAlt /> Verify your identity through secure methods</li>
              </ul>
              
              {formData.verificationStatus === "processing" && (
                <div className="verification-status processing">
                  <FaSpinner className="spinner" />
                  <span>Verifying your identity...</span>
                </div>
              )}
            </div>
          )}
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={isLoading || formData.verificationStatus === "processing"}
          >
            {isLoading ? (
              <>
                <FaSpinner className="spinner" /> 
                Processing...
              </>
            ) : (
              verificationStep === 1 ? (
                <>
                  <FaCheckCircle className="button-icon" />
                  Continue
                </>
              ) : (
                <>
                  <FaCheckCircle className="button-icon" />
                  Start Verification
                </>
              )
            )}
          </button>
        </form>
      </div>
    </OnboardingLayout>
  );
};

export default AgeVerification;